import { useState, useRef } from "react";

interface DraggableImageGridProps {
  images: string[];
  onReorder: (images: string[]) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
  maxCount?: number;
}

export default function DraggableImageGrid({
  images,
  onReorder,
  onRemove,
  onAdd,
  maxCount = 6,
}: DraggableImageGridProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragItem = useRef<number | null>(null);

  const handleDragStart = (index: number) => {
    dragItem.current = index;
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIdx = dragItem.current;
    if (dragIdx === null || dragIdx === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const [removed] = newImages.splice(dragIdx, 1);
    newImages.splice(dropIndex, 0, removed);
    onReorder(newImages);

    setDragIndex(null);
    setDragOverIndex(null);
    dragItem.current = null;
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
    dragItem.current = null;
  };

  return (
    <div className="flex flex-wrap gap-3">
      {images.map((img, idx) => (
        <div
          key={`img-${img.substring(img.length - 20)}-${idx}`}
          draggable
          onDragStart={() => handleDragStart(idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, idx)}
          onDragEnd={handleDragEnd}
          className={`relative w-24 h-24 rounded-lg overflow-hidden bg-gray-100 border-2 transition-all cursor-grab active:cursor-grabbing ${
            dragIndex === idx
              ? "opacity-40 scale-95 border-dashed border-blue-400"
              : dragOverIndex === idx
              ? "border-blue-500 scale-105 shadow-lg"
              : "border-gray-200"
          }`}
        >
          <img
            src={img}
            alt={`img-${idx}`}
            className="w-full h-full object-cover pointer-events-none"
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' fill='%239ca3af'%3E图片加载失败%3C/text%3E%3C/svg%3E";
            }}
          />
          {/* 顺序标号 */}
          <span className="absolute top-1 left-1 w-5 h-5 flex items-center justify-center rounded bg-black/60 text-white text-xs font-medium">
            {idx + 1}
          </span>
          {/* 删除按钮 */}
          <button
            type="button"
            onClick={() => onRemove(idx)}
            className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 cursor-pointer"
          >
            <i className="ri-close-line text-xs" />
          </button>
          {/* 封底标注 */}
          {idx === 0 && (
            <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-orange-500 text-white text-xs rounded">
              封面
            </span>
          )}
        </div>
      ))}
      {images.length < maxCount && (
        <button
          type="button"
          onClick={onAdd}
          className="w-24 h-24 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 hover:border-orange-400 hover:bg-orange-50 transition-colors cursor-pointer"
        >
          <i className="ri-camera-line text-xl text-gray-400" />
          <span className="text-xs text-gray-400 mt-1">添加图片</span>
        </button>
      )}
      {/* 拖拽提示 */}
      {images.length > 1 && !dragIndex && (
        <div className="w-full mt-1">
          <p className="text-xs text-gray-400">
            <i className="ri-drag-move-line mr-1" />
            拖拽图片可调整顺序，第一张为封面图
          </p>
        </div>
      )}
    </div>
  );
}
